// IMPORT REACT
import React, {
  useContext,
  useEffect,
  useRef,
  useState
} from 'react'

// IMPORT UI COMPONENTS
import {
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  IconButton,
  Menu,
  MenuItem,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme
} from '@mui/material'

import IosShareIcon from '@mui/icons-material/IosShare';
import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import logo from "../../assets/images/logo.png"
import CloseIcon from "@mui/icons-material/Close";
import DownloadIcon from "@mui/icons-material/Download";
import ReferBook from './ReferBook';
import {
  GetReferBookEntryDetail,
  GetReferBookTokenDetail
} from '../../services/ReferBookEntryService';
import { PreferencesContext } from '../../PreferenceContext';
import { AlertType } from '../../services/models/AlertType';
import { useQuery } from '../../shared/CommonFunction';
import { EncryptDecryptService } from '../../services/common/EncryptionService';
import html2pdf from "html2pdf.js";
import { useNavigate } from 'react-router-dom';
import PrintIcon from "@mui/icons-material/Print";
import { printHtmlContent } from "../../shared/iosPwaPrint";
import RemoveRedEyeIcon from '@mui/icons-material/RemoveRedEye';
import { SaveNotificationLog } from '../../services/TokenService';
import { UI_APP_URL } from '../../services/common/const';
import html2canvas from "html2canvas";
import { GenerateToken } from '../../services/ShortLinkService';

interface BranchDetails {
  consumerId: number;
  organizationId: number;
  branchId: number;
  doctorId: number;
  fullName: string;
  qualification: string;
}

const ReferBookThankYou = (Props: any) => {
  // PROPS
  const {
    isForPopUp = false,
  } = Props;

  const initialBranchDetails: BranchDetails = {
    consumerId: 0,
    organizationId: 0,
    branchId: 0,
    fullName: '',
    qualification: '',
    doctorId: 0
  };

  const theme = useTheme();
  const edService = new EncryptDecryptService();
  const navigate = useNavigate();

  const smallScreen = useMediaQuery("(max-width:768px)");
  const fullScreen = useMediaQuery(theme.breakpoints.down("md"));
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const { userContext, updateUserContext } = useContext(PreferencesContext);
  const [branchDetails, setBranchDetails] = useState<BranchDetails>(initialBranchDetails);
  const [referBookData, setReferBookData] = useState<any>();
  const htmlprintRef = useRef<HTMLDivElement | null>(null);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [userIpAddress, setUserIpAddress] = useState("");

  const [customPopUp, setCustomPopUp] = React.useState({
    open: false,
    name: "",
    code: "",
  });

  // GETTTING PARAMS
  let urlParams = useQuery();
  let token = urlParams.get("t");
  let currentToken: string = '';

  if (token !== null && token !== undefined) {
    currentToken = token;
  } else {
    navigate('/not-found')
  }

  let referBookEntryId = urlParams.get("q");
  let curReferBookEntryId = 0;
  if (referBookEntryId !== null && referBookEntryId !== undefined) {
    let decId = edService.decrypt(referBookEntryId);
    if (
      decId !== "" &&
      decId !== null &&
      decId !== undefined &&
      isForPopUp === false
    ) {
      curReferBookEntryId = +decId;
    }
  } else {
    navigate('/not-found')
  }

  const handleClose = () => {
    setCustomPopUp({ ...customPopUp, open: false });
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const open = Boolean(anchorEl);

  const handleDownloadPdf = () => {
    const element = htmlprintRef.current;
    const documentName = referBookData?.forPatientFirstName
      ? `${referBookData.forPatientFirstName}_${referBookData.forPatientLastName || ""}`
      : "ReferBook";

    html2pdf()
      .set({
        margin: [10, 10, 10, 10],
        image: { type: "jpeg", quality: 1 },
        html2canvas: {
          scale: 10,
          scrollY: 0,
          width: 794,
          minHeight: 1123,
        },
        filename: `${documentName}.pdf`,
        jsPDF: { unit: "mm", format: "a4", orientation: "portrait" },
        pagebreak: {
          mode: ["css", "legacy"],
        },
      })
      .from(element)
      .save();

    handleMenuClose();
  };

  const handleDownloadJpg = async () => {
    if (!referBookData) {
      await getReferBookEntryDetailById();
    }

    const tempDiv = document.createElement('div');
    tempDiv.style.position = 'absolute';
    tempDiv.style.left = '-9999px';
    tempDiv.style.width = '794px';
    document.body.appendChild(tempDiv);

    const root = await import('react-dom/client');
    const reactRoot = root.createRoot(tempDiv);
    const ReferBookModule = await import('./ReferBook');
    const ReferBookComponent = ReferBookModule.default;

    await new Promise<void>((resolve) => {
      reactRoot.render(
        React.createElement(ReferBookComponent, { data: referBookData })
      );
      setTimeout(resolve, 300);
    });

    const documentName = referBookData?.forPatientFirstName
      ? `${referBookData.forPatientFirstName}_${referBookData.forPatientLastName || ""}`
      : "ReferBook";

    const wrapper = document.createElement("div");
    wrapper.style.display = "inline-block";
    wrapper.style.padding = "10mm";
    wrapper.style.background = "#ffffff";
    wrapper.style.boxSizing = "content-box";

    const inner = document.createElement("div");
    inner.style.width = "794px";
    inner.style.background = "#ffffff";
    inner.style.boxSizing = "border-box";

    inner.appendChild(tempDiv.firstChild!.cloneNode(true));
    wrapper.appendChild(inner);
    document.body.appendChild(wrapper);

    const canvas = await html2canvas(wrapper, {
      scale: 3,
      useCORS: true,
      backgroundColor: "#ffffff",
      windowWidth: wrapper.scrollWidth,
      windowHeight: wrapper.scrollHeight
    });

    document.body.removeChild(wrapper);
    reactRoot.unmount();
    document.body.removeChild(tempDiv);

    const imageData = canvas.toDataURL("image/jpeg", 1.0);
    const link = document.createElement("a");
    link.href = imageData;
    link.download = `${documentName}.jpg`;
    link.click();

    handleMenuClose();
  };

  const handlePrintHtml = () => {
    if (htmlprintRef.current) {
      printHtmlContent(htmlprintRef.current, {
        printable: htmlprintRef.current.innerHTML,
        type: "raw-html",
        maxWidth: 1000,
        style: `@media print {
          body
            {
              padding: 0;
              background-color: #fff;
              -webkit-print-color-adjust: exact;
              print-color-adjust: exact;
              font-family: Arial, sans-serif !important; /* Set font family */
            }
    
             @page {
              size: auto;
              margin:0.80in;
            }
    
            .print-conatiner {
              display: flex;
              flex-direction: column;
              min-height:100vh;
              position:relative;
            }
    
            .page-break {
              page-break-before: always;
              top: 100px;                      
            }
    
            .address-container {
              display: flex;
              justify-content: center;
              align-items: center;
              text-align: center;
              font-size: 12.5px;
            }
    
            .patientDetails {
              margin-top: 0 !important;
              padding-top: 0 !important;
              font-size: 12px !important;
            }
    
            .content{
              flex-grow:1;
            }
    
            .footer {
              // position: fixed;
              bottom: 0;
              left: 0;
              width: 100%;
            }
            .footer::after{
              content:'',
              display: block;
              height: 50px;
            }
              
            * {
                font-family: Arial, sans-serif !important; /* Ensure all elements use Arial */
                margin: 0;
                padding: 0;
                box-sizing: border-box;
              }
          }
        `,
        scanStyles: true,
        targetStyles: ["*"],
      });
    }
  };

  const handlePdfPrint = () => {
    handlePrintHtml();
  };

  const getReferBookTokenDetail = async () => {
    try {
      let tokenData: any = null;
      const params = new URLSearchParams();
      params.append("token", token || "");

      const result: any = await GetReferBookTokenDetail(params, currentToken);

      if (result?.data?.data) {
        const data = result.data.data;

        tokenData = {
          consumerId: data.consumerId ?? 0,
          organizationId: data.organizationId ?? 0,
          doctorId: data.doctorId ?? 0,
          branchId: data.branchId ?? 0,
          fullName: data.fullName ?? '',
          qualification: data.qualification ?? '',
        };

        setBranchDetails(tokenData);
      }
      return tokenData;
    } catch (e: any) {
      updateUserContext({
        ...userContext,
        isAlert: true,
        alertMessage:
          typeof e === "string" ? (e as string) : (e?.message as string),
        alertType: AlertType.Error,
      });
    }
  };

  const getReferBookEntryDetailById = async (tokenData?: any) => {
    let referBookEntry: any;
    let params = new URLSearchParams();
    params.append("referBookEntryId", curReferBookEntryId?.toString());
    try {
      const res: any = await GetReferBookEntryDetail(params, currentToken);
      if (res?.data?.data) {
        referBookEntry = res?.data?.data;
        setReferBookData(referBookEntry);
      }
    } catch (e: any) {
      updateUserContext({
        ...userContext,
        isAlert: true,
        alertMessage:
          typeof e === "string" ? (e as string) : (e?.message as string),
        alertType: AlertType.Error,
      });
    } finally {
      return referBookEntry;
    }
  };


  const handleClickOnWhatsApp = (item: any) => {
    if (!item) return;
    const encId = edService.encrypt(item?.referBookEntryId.toString()) ?? "";  // <--- encrypt referBookEntryId
    const patientName = `${item.forPatientFirstName || ""} ${item.forPatientLastName || ""}`.trim();
    const fromDoctorName = item.fromDoctorName || "";
    const fromDoctorQualification = item.fromDoctorQualification ? ` (${item.fromDoctorQualification})` : "";
    const toDoctorName = item.toDoctorName || "";
    const toDoctorQualification = item.toDoctorQualification ? ` (${item.toDoctorQualification})` : "";
    const reasonForReference = item.reasonForReferenceColl
      ?.sort((a: any, b: any) => a.displayOrder - b.displayOrder)
      ?.map((i: any) => i.reasonForReferenceLabel)
      ?.join(", ") || "";

    const address = [
      item?.toLocationAddress1,
      item?.toLocationAddress2,
      item?.toLocationCity,
      item?.toLocationCountry
    ]
      .filter(Boolean)
      .join(", ") + (item?.toLocationPostalCode ? ` - ${item?.toLocationPostalCode}` : "");

    const pdfUrl = `${item?.refBookRedirectPath}`;
    const appointmentLink = `${item?.appointmentRedirectPath || ''}`

    const message = `*${fromDoctorName}${fromDoctorQualification}* has advised *${patientName}* to consult *${toDoctorName}${toDoctorQualification}* 
    for *${reasonForReference}* \n\n📍Location: *${item?.toLocationName}* \n${address}.\n\n📖 Click here to view Refer Book: \n${pdfUrl} 
    \n\n📅 Click here to book an appointment: \n${appointmentLink}`;

    const encodedMessage = encodeURIComponent(message);

    const phone = item?.forPatientMobileNumber || item?.toLocationContactNumber || "";

    const isMobile = /iPhone|Android|webOS|BlackBerry|iPad|iPod/.test(navigator.userAgent);

    let whatsappUrl = "";

    if (phone) {
      whatsappUrl = `https://${isMobile ? "api" : "web"}.whatsapp.com/send?phone=${phone}&text=${encodedMessage}`;
    } else {
      whatsappUrl = `https://${isMobile ? "api" : "web"}.whatsapp.com/send?text=${encodedMessage}`;
    }

    // SAVE NOTIFICATION LOG BEFORE OPENING WHATSAPP
    // await saveNotificationLog(message, phone);

    window.open(whatsappUrl, "_blank");
  };

  const generateToken = async (data: {
    type: string, actionType: string, redirectPath: string, remarks: string, linkId: number,
    tableName: string
  }) => {
    let token = null;
    try {
      const tokenData = {
        consumerId: referBookData?.consumerId,
        orgId: referBookData?.organizationId,
        branchId: referBookData?.branchId,
        redirectPath: data.redirectPath,
        type: data.type,
        actionType: data.actionType,
        linkId: data.linkId,
        tableName: data.tableName,
        expiresOn: null,
        expiryInDays: 30,
        ipAddress: userIpAddress || '',
        deviceInfo: navigator.userAgent,
        remarks: data.remarks,
        tokenData: {
          o: referBookData?.organizationId,
          c: referBookData?.consumerId,
          b: referBookData?.branchId
        },
        dataManagedBy: 0,
      };

      const result: any = await GenerateToken(tokenData);
      if (result?.data) {
        token = result?.data;
      }
    } catch (e: any) {
      updateUserContext({
        ...userContext,
        isAlert: true,
        alertMessage: e?.message || "Token generation failed",
        alertType: AlertType.Error,
      });
    } finally {
      return token;
    }
  };

  // FETCH DATA
  const fetchData = async () => {
    try {
      const tokenData = await getReferBookTokenDetail();
      if (tokenData && curReferBookEntryId > 0) {
        await getReferBookEntryDetailById(tokenData);
      }
      setIsLoading(false);
    } catch (e) {
      setIsLoading(false);
    }
  };

  // USE EFFECTS
  useEffect(() => {
    setIsLoading(true);
    fetchData();
  }, [referBookEntryId]);

  // FETCH IP ADDRESS
  useEffect(() => {
    fetch('https://api.ipify.org?format=json')
      .then(response => response.json())
      .then(data => setUserIpAddress(data.ip))
      .catch(() => setUserIpAddress(""));
  }, []);

  return (
    <>
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "95vh",
          paddingY: 6,
        }}
      >
        <Box
          sx={{
            width: "100%",
            maxWidth: 500,
            backgroundColor: "#fff",
            borderRadius: "10px",
            boxShadow: "0 4px 15px rgba(0,0,0,0.08)",
            padding: 5,
            textAlign: "center",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
          }}
        >
          <Box
            sx={{
              width: 60,
              height: 60,
              borderRadius: "50%",
              backgroundColor: "#eff3fd",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              mb: 3,
            }}>
            <IosShareIcon sx={{ fontSize: 64, color: "#285cdb" }} />
          </Box>

          <Typography
            sx={{
              fontWeight: 600,
              fontSize: "20px !important",
            }}>
            Thank you, <br />
            your patient has been referred!
          </Typography>

          <Grid
            container
            spacing={2}
            justifyContent="center"
            alignItems="center"
            mt={2}
          >
            <Grid item>
              <Button
                variant="contained"
                className="primary-button"
                startIcon={<WhatsAppIcon sx={{ color: "#fff" }} />}
                onClick={async () => {
                  let refBook = referBookData;
                  if (!refBook?.refBookRedirectPath || !refBook?.appointmentRedirectPath) {
                    setIsLoading(true);
                    if (!refBook?.refBookRedirectPath) {
                      let token = await generateToken({
                        type: 'REFERBOOK',
                        actionType: 'REFERBOOK_LINK',
                        redirectPath: '/refer-book-report',
                        remarks: 'Refer Book Link',
                        linkId: refBook.referBookEntryId,
                        tableName: 'ReferBookEntry'
                      });
                      refBook = { ...refBook, refBookRedirectPath: token?.targetUrl ?? '', refBookShortToken: token?.shortCode ?? '' };
                    }
                    if (!refBook?.appointmentRedirectPath) {
                      let token = await generateToken({
                        type: 'APPOINTMENT_LINK',
                        actionType: 'BOOK_APPOINTMENT',
                        redirectPath: '/appointment-booking',
                        remarks: 'Appointment Booking Token',
                        linkId: refBook.branchId,
                        tableName: 'Branch'
                      });
                      refBook = { ...refBook, appointmentRedirectPath: token?.targetUrl ?? '', appointmentShortToken: token?.shortCode ?? '' };
                    }
                    setReferBookData((prevData: any) => ({ ...prevData, ...refBook }));
                    setIsLoading(false);
                  }
                  await handleDownloadJpg();
                  handleClickOnWhatsApp(refBook);
                }}
                disabled={isLoading}
              >
                Send to Patient
              </Button>
            </Grid>

            {/* <Grid item>
              <Button
                color="primary"
                variant="contained"
                className="primary-button"
                startIcon={<RemoveRedEyeIcon sx={{ color: "#fff" }} />}
                onClick={async (e) => {
                  setIsLoading(true);
                  await getReferBookEntryDetailById();
                  setCustomPopUp({
                    open: true,
                    code: "REFERRAL_BOOK",
                    name: ""
                  });
                  setIsLoading(false);
                }}
              >
                View Refer Book
              </Button>
            </Grid> */}
          </Grid>
        </Box>
      </Box>

      {/* Footer */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          px: 3,
          pb: 2,
          flexDirection: smallScreen ? "column" : "row",
          gap: smallScreen ? 1 : 0,
        }}>
        <Typography className="font-size-11">
          Copyrights &copy; {new Date().getFullYear()} - Pediaverse
          Solutions Pvt Ltd
        </Typography>

        {/* Powered by + Logo */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: 0.5,
          }}
        >
          <Typography className="font-size-11" sx={{ whiteSpace: "nowrap", marginTop: "2px" }}>
            Powered by
          </Typography>
          <a href='/' target='_blank'>
            <img
              src={logo}
              alt="Logo"
              style={{
                height: 20,
                width: "auto",
                objectFit: "contain",
                verticalAlign: "middle",
                cursor: "pointer"
              }}
            />
          </a>
        </Box>

        <Typography className="font-size-11">
          <a
            href="/terms"
            target="_blank"
            className="blue-link"
            style={{ textDecoration: "none" }}>
            Terms of Service
          </a>{" "}
          &nbsp;
          <a
            href="/privacy-policy"
            target="_blank"
            className="blue-link"
            style={{ textDecoration: "none" }}>
            Privacy Policy
          </a>
        </Typography>
      </Box>

      {/* REFERRAL_BOOK */}
      <Dialog
        fullScreen={fullScreen}
        open={
          customPopUp.open &&
          customPopUp.code === "REFERRAL_BOOK"
        }
        onClose={(e, reason) => {
          if (reason !== "backdropClick") {
            handleClose();
          }
        }}
        scroll="paper"
        PaperProps={{
          sx: {
            borderRadius: "4px",
            maxWidth: "950px",
            margin: "auto",
            overflow: "hidden",
          },
        }}>
        <DialogTitle
          sx={{
            borderBottom: "1px solid #ddd",
            padding: "4px 16px",
          }}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}>

            <Typography fontWeight={600}>
              Refer Book
            </Typography>

            <Box display="flex" alignItems="center">
              <Tooltip title="Print PDF" placement="top">
                <IconButton
                  aria-label="Print PDF"
                  onClick={handlePdfPrint}
                >
                  <PrintIcon />
                </IconButton>
              </Tooltip>

              <Tooltip title="Download" placement="top">
                <IconButton
                  aria-label="Download"
                  onClick={handleDownloadJpg}
                >
                  <DownloadIcon />
                </IconButton>
              </Tooltip>

              <Tooltip title="Close" placement="top">
                <IconButton
                  aria-label="close"
                  onClick={handleClose}
                >
                  <CloseIcon />
                </IconButton>
              </Tooltip>
            </Box>
          </Box>
        </DialogTitle>

        <DialogContent
          sx={{
            height: "100%",
            padding: "16px !important",
            fontFamily: "Arial, sans-serif",
            backgroundColor: "#f1f3f5",
            boxSizing: "border-box",
          }}>
          <Box
            sx={{
              width: "max-content",
              border: "1px solid #ddd",
              boxShadow: "4px rgba(0, 0, 0, 0.2)",
              borderRadius: "2px",
              padding: "30px 50px",
              fontFamily: "Arial, sans-serif",
              backgroundColor: "white",
              boxSizing: "border-box",
            }}
          >
            <Box ref={htmlprintRef} sx={{ width: "794px" }}>
              {referBookData && (
                <ReferBook data={referBookData} />
              )}
            </Box>
          </Box>
        </DialogContent>
      </Dialog>

      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleMenuClose}
        PaperProps={{
          sx: {
            border: "1px solid #eee",
          },
        }}
      >
        <MenuItem onClick={handleDownloadPdf}>
          Export as PDF
        </MenuItem>
        <Divider />
        <MenuItem onClick={handleDownloadJpg}>
          Export as JPG
        </MenuItem>
      </Menu>
    </>
  )
}

export default ReferBookThankYou
